import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Heart, Trash2 } from "lucide-react";
import type { DecisionItem } from "../types";

const KIND_TONE: Record<string, string> = {
  tool: "bg-pop-teal", category: "bg-pop-orange text-white", stuck: "bg-pop-yellow", present: "bg-pop-pink text-white", group: "bg-pop-purple text-white",
};

export function DecisionRow({ item, fav, onFav, onRemove, index }: { item: DecisionItem; fav?: boolean; onFav?: () => void; onRemove?: () => void; index: number }) {
  return (
    <li className="flex items-center gap-3 rounded-2xl border-2 border-ink bg-white p-4" data-testid={`decision-row-${index}`}>
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span className={`rounded-full border-2 border-ink px-2 py-0.5 text-[10px] font-extrabold uppercase ${KIND_TONE[item.kind] || "bg-white"}`}>{item.kind}</span>
          <span className="text-xs font-semibold text-zinc-500">{formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}</span>
        </div>
        <p className="truncate text-xs font-bold uppercase text-zinc-500">{item.title}</p>
        <p className="font-display text-lg font-bold leading-tight [overflow-wrap:anywhere]">{item.result}</p>
      </div>
      {onFav && (
        <button aria-label="Toggle favourite" onClick={onFav} className="rounded-full border-2 border-ink p-2 transition-transform active:scale-90" data-testid={`decision-fav-button-${index}`}>
          <Heart className={`h-4 w-4 ${fav ? "fill-pop-pink text-pop-pink" : ""}`} strokeWidth={3} />
        </button>
      )}
      {onRemove && (
        <button aria-label="Remove" onClick={onRemove} className="rounded-full border-2 border-ink p-2 hover:bg-pop-pink hover:text-white" data-testid={`decision-remove-button-${index}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </li>
  );
}

export function GuestBanner() {
  return (
    <div className="mb-5 rounded-2xl border-2 border-dashed border-ink bg-pop-yellow/40 p-4 text-sm font-semibold" data-testid="guest-data-banner">
      You're a guest — this lives in this browser only and disappears if you clear it.{" "}
      <Link to="/auth" className="font-extrabold underline" data-testid="guest-banner-signin-link">Sign in to keep it forever.</Link>
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[22px] border-[3px] border-dashed border-ink/40 p-10 text-center" data-testid="empty-state">
      <p className="font-display text-2xl font-bold uppercase">{title}</p>
      <p className="mt-1 font-medium text-zinc-600">{body}</p>
      <Link to="/stuck" className="btn-pop mt-5 inline-flex rounded-2xl bg-pop-yellow px-5 py-3 font-display font-bold uppercase" data-testid="empty-state-stuck-link">I'm stuck</Link>
    </div>
  );
}
